import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import { message } from 'antd'
import SaveIcon from '@mui/icons-material/Save'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import Helmet from '../../../../../../components/Helmet'
import HeaderContent from '../../../../../../components/Layouts/HeaderContent'
import { ContentWrapper } from '../../../../../../components/ContentWrapper'
import { axiosInstance } from '../../../../../../hooks/useAxios'
import CircularLoading from '../../../../../../components/CircularProgress'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../../constants/errors'
import { ImageLinks } from '../../../../../../constants/image-link'
import { Box } from '@mui/material'
import { clearMerchantCache } from '../../../../../../utils/cache'

const Menus: any[] = []

interface MerchantProviders {
  flash?: {
    enabled: boolean
    username: string
    password: string
    merchantId: string
    merchantParentId: string
    merchantParentKey: string
  }
  jnt?: {
    enabled: boolean
    shopId: string
    shopName: string
  }
  kerry?: {
    enabled: boolean
    merchantId: string
    kerryId: string
    kerryKey: string
  }
  thaiPost?: {
    enabled: boolean
  }
  bestExpress?: {
    enabled: boolean
  }
  spx?: {
    enabled: boolean
    appId: string
    appSecret: string
    userId: string
    userSecret: string
  }
}

const ShippingSettingsListPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [providers, setProviders] = useState<MerchantProviders>({})
  const [isFlashAccountModalOpen, setIsFlashAccountModalOpen] = useState(false)
  const [flashAccountUrl, setFlashAccountUrl] = useState('')

  useEffect(() => {
    const fetchProviders = async () => {
      setIsLoading(true)
      try {
        const response = await axiosInstance.get<{
          providers: MerchantProviders
        }>('/merchant/providers')
        const { data } = response
        setProviders(data.providers || {})
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'ดึงข้อมูลไม่สำเร็จ'
        message.error(messageError)
      }
      setIsLoading(false)
    }
    fetchProviders()
  }, [])

  const handleUpdateProvider = useCallback(
    (providerKey: keyof MerchantProviders, field: string, value: any) => {
      setProviders((prev) => {
        const currentProvider = prev[providerKey] || {}
        return {
          ...prev,
          [providerKey]: {
            ...currentProvider,
            [field]: value,
          },
        }
      })
    },
    [],
  )

  const isProviderEnabled = (provider: any): boolean => {
    return provider?.enabled === true
  }

  const handleSave = async () => {
    setIsLoading(true)
    try {
      await axiosInstance.put('/merchant/update/providers', {
        providers,
      })
      clearMerchantCache()
      message.success('บันทึกข้อมูลขนส่งสำเร็จ')
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoading(false)
  }

  const handleGenLinkFlashAccount = async () => {
    setIsLoading(true)
    try {
      const response = await axiosInstance.get<{ url: string }>(
        '/merchant/gen-link-flash-account',
      )
      const { url } = response.data
      setFlashAccountUrl(url)
      setIsFlashAccountModalOpen(true)
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'สร้างลิงก์เชื่อมต่อ Flash Account ไม่สำเร็จ'
      message.error(messageError)
    }
    setIsLoading(false)
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(flashAccountUrl)
    message.success('คัดลอกลิงก์เรียบร้อย')
  }

  const handleOpenUrl = () => {
    window.open(flashAccountUrl, '_blank')
  }

  return (
    <ContentWrapper>
      <Helmet title="ตั้งค่าขนส่ง" />
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="ตั้งค่าขนส่ง"
            routes={Menus}
            descriptions="ตั้งค่าขนส่ง"
          />
        </Grid>
        <Grid item mb={2}>
          <Button
            variant="contained"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isLoading}
          >
            บันทึกข้อมูล
          </Button>
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <CircularLoading isLoading={isLoading} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {isProviderEnabled(providers.flash) && (
                <Grid item xs={12}>
                  <Card sx={{ padding: 2, height: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <img
                        src={ImageLinks.LOGO_FLASH}
                        alt="Flash Express"
                        style={{
                          width: '100px',
                          height: '60px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                        }}
                      />
                      <h3 style={{ margin: 0, fontSize: '18px' }}>
                        Flash Express
                      </h3>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mb: 1,
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleGenLinkFlashAccount}
                        disabled={isLoading}
                      >
                        เชื่อม Flash Account
                      </Button>
                    </Box>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Username"
                        value={providers.flash?.username || ''}
                        onChange={(e) =>
                          handleUpdateProvider(
                            'flash',
                            'username',
                            e.target.value,
                          )
                        }
                        disabled={!isProviderEnabled(providers.flash)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="password"
                        label="Password"
                        value={providers.flash?.password || ''}
                        onChange={(e) =>
                          handleUpdateProvider(
                            'flash',
                            'password',
                            e.target.value,
                          )
                        }
                        disabled={!isProviderEnabled(providers.flash)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Merchant ID"
                        value={providers.flash?.merchantId || ''}
                        onChange={(e) =>
                          handleUpdateProvider(
                            'flash',
                            'merchantId',
                            e.target.value,
                          )
                        }
                        disabled={!isProviderEnabled(providers.flash)}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              )}

              {isProviderEnabled(providers.jnt) && (
                <Grid item xs={12}>
                  <Card sx={{ padding: 2, height: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <img
                        src={ImageLinks.LOGO_JNT}
                        alt="J&T Express"
                        style={{
                          width: '100px',
                          height: '60px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                        }}
                      />
                      <h3 style={{ margin: 0, fontSize: '18px' }}>J&T Express</h3>
                    </Box>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Shop ID"
                        value={providers.jnt?.shopId || ''}
                        onChange={(e) =>
                          handleUpdateProvider('jnt', 'shopId', e.target.value)
                        }
                        disabled={!isProviderEnabled(providers.jnt)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Shop Name"
                        value={providers.jnt?.shopName || ''}
                        onChange={(e) =>
                          handleUpdateProvider(
                            'jnt',
                            'shopName',
                            e.target.value,
                          )
                        }
                        disabled={!isProviderEnabled(providers.jnt)}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              )}

              {isProviderEnabled(providers.thaiPost) && (
                <Grid item xs={12}>
                  <Card sx={{ padding: 2, height: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <img
                        src={ImageLinks.LOGO_THAIPOST}
                        alt="Thai Post"
                        style={{
                          width: '100px',
                          height: '60px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                        }}
                      />
                      <h3 style={{ margin: 0, fontSize: '18px' }}>Thai Post</h3>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2, mb: 1 }}
                    >
                      บริการขนส่งไปรษณีย์ไทยพร้อมใช้งานแล้ว
                      คุณสามารถใช้งานบริการได้ทันทีโดยไม่ต้องกรอกข้อมูลเพิ่มเติม
                    </Typography>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {isProviderEnabled(providers.kerry) && (
                <Grid item xs={12}>
                  <Card sx={{ padding: 2, height: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <img
                        src={ImageLinks.LOGO_KERRY}
                        alt="Kerry Express"
                        style={{
                          width: '100px',
                          height: '60px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                        }}
                      />
                      <h3 style={{ margin: 0, fontSize: '18px' }}>
                        Kerry Express
                      </h3>
                    </Box>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="เบอร์วอเลตที่สมัคร KEX"
                        value={providers.kerry?.merchantId || ''}
                        onChange={(e) =>
                          handleUpdateProvider(
                            'kerry',
                            'merchantId',
                            e.target.value,
                          )
                        }
                        disabled={!isProviderEnabled(providers.kerry)}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              )}

              {isProviderEnabled(providers.bestExpress) && (
                <Grid item xs={12}>
                  <Card sx={{ padding: 2, height: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <img
                        src={ImageLinks.LOGO_BESTEXPRESS}
                        alt="Best Express"
                        style={{
                          width: '100px',
                          height: '60px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                        }}
                      />
                      <h3 style={{ margin: 0, fontSize: '18px' }}>
                        Best Express
                      </h3>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2, mb: 1 }}
                    >
                      บริการขนส่ง Best Express พร้อมใช้งานแล้ว
                      คุณสามารถใช้งานบริการได้ทันทีโดยไม่ต้องกรอกข้อมูลเพิ่มเติม
                    </Typography>
                  </Card>
                </Grid>
              )}

              {isProviderEnabled(providers.spx) && (
                <Grid item xs={12}>
                  <Card sx={{ padding: 2, height: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1,
                      }}
                    >
                      <img
                        src={ImageLinks.LOGO_SPXEXPRESS}
                        alt="SPX Express"
                        style={{
                          width: '100px',
                          height: '60px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                        }}
                      />
                      <h3 style={{ margin: 0, fontSize: '18px' }}>SPX Express</h3>
                    </Box>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="User ID"
                        value={providers.spx?.userId || ''}
                        onChange={(e) =>
                          handleUpdateProvider('spx', 'userId', e.target.value)
                        }
                        disabled={!isProviderEnabled(providers.spx)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="User Secret"
                        value={providers.spx?.userSecret || ''}
                        onChange={(e) =>
                          handleUpdateProvider(
                            'spx',
                            'userSecret',
                            e.target.value,
                          )
                        }
                        disabled={!isProviderEnabled(providers.spx)}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Card>

      {/* Flash Account Connection Modal */}
      <Dialog
        open={isFlashAccountModalOpen}
        onClose={() => setIsFlashAccountModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>เชื่อมต่อ Flash Account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            กรุณาเปิดลิงก์ด้านล่างเพื่อเชื่อมต่อบัญชี Flash Express ของคุณ
            หลังจากเชื่อมต่อสำเร็จ ข้อมูลจะถูกอัปเดตอัตโนมัติ
          </Typography>
          <TextField
            fullWidth
            label="ลิงก์เชื่อมต่อ"
            value={flashAccountUrl}
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyUrl} edge="end">
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyUrl}
            >
              คัดลอกลิงก์
            </Button>
            <Button
              variant="contained"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenUrl}
            >
              เปิดลิงก์
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFlashAccountModalOpen(false)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  )
}

export default ShippingSettingsListPage
